import { DeviceHelpers } from "../helpers/device-helpers";
import { FormatHelpers } from "../helpers/format-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { IChartDataService } from "../models/base/chart-data-service.interface";
import { Timeframe } from "../models/enum/timeframe.enum";
import { CurrentPositionDataModel } from "../models/pnl/current-position-data.model";
import { CurrentPositionViewModel } from "../models/pnl/current-position-view-model";
import { PnlDataModel } from "../models/pnl/pnl-data.model";
import { TradeAccountModel } from "../models/trade/trade-account.model";
import { QuoteType } from "../models/trade/trade.model";
import { AdvancedTradeComponent } from "./advanced-trade.component";
import { IComponent } from "./component.interface";
import { DrawExitOrderComponent } from "./exit-orders/draw-exit-order.component";
import { QuickOrdersComponent } from "./menu/quick-orders.component";
import { TradeOrderTypeComponent } from "./menu/trade-order-type.component";
import { TradeComponent } from "./trade.component";

export class PnlComponent implements IComponent {
    public id = 'pnl-component';

    public element: HTMLElement;
    private base: IChartContainer;
    private chartService: IChartDataService;

    private lastPrice = 0;
    public selectedAccount = '';
    public lastTradeAccount = '';
    private preSelectedAccount = '';
    private symbol = '';
    public preSelectedShares = 0;
    public preSelectedQuoteType = QuoteType.Dollars;
    private isSelectedAccountClosed = false;
    private preSelectedInited = false;
    private pnlClickInited = false;
    private selectedAccountFlatten = false;

    private enableExitOrder = false;
    private currentPositionViewModels: Array<CurrentPositionViewModel> = [];
    private accounts: Array<TradeAccountModel> = [];

    constructor(el: HTMLElement, base: IChartContainer) {
        this.element = el;
        this.base = base;
        this.chartService = base.chartService;
        this.subscribeChartDataService();
        this.onResize();

        if (!this.base.marketingWebChartMode) {
            this.enableExitOrder = true;
        }

        if (DeviceHelpers.isIOS()) {
            this.element.addClass('aapl');
        }
    }

    public hideElements() {
        this.pnlClickInited = false;
        this.hideCurrentPositionElement();
        this.hidePnlDataElement();
        this.addOneClass();
        this.displayExitOrder(true);
    }

    public enableAnnotations() {
        this.enableExitOrder = true;
        this.displayExitOrder(false);
    }

    public disableAnnotations() {
        this.enableExitOrder = false;
        this.displayExitOrder(true);
    }

    public getOpenPosition(): CurrentPositionViewModel | null {
        return this.currentPositionViewModels.find(x => x.shares != 0) ?? null;
    }

    public getSelectedPosition(): CurrentPositionViewModel | null {
        return this.currentPositionViewModels.find(x => x.accountName == this.selectedAccount) ?? null;
    }

    public isSelectedAccountFlatten(): boolean {
        return this.selectedAccountFlatten;
    }

    public addOneClass() {
        const menuBox = this.element.closest('.pnl-box');
        let show = false;

        if (menuBox) {
            menuBox.querySelectorAll<HTMLElement>('& > div').forEach((el: HTMLElement) => {
                if (el.clientHeight) {
                    show = true;
                }
            });
        }

        if (show) {
            this.base.element.classList.remove('one');
        } else {
            this.base.element.classList.add('one');
        }
    }

    public setSelectedAccountFromCurrentPositionActiveElement() {
        this.element.findParents('.current-position-container.active')?.ifExistThen((el: HTMLElement) => {
            const accountName = el.getAttribute('data-account-name');
            if (accountName) {
                this.selectedAccount = accountName;
            }
        });
    }

    public setSelectPositionByAccount(accountName: string) {
        if (!accountName) return;

        const existingPositionAccount = this.element.findFirst('.current-position-container[data-account-name="' + accountName + '"]');
        if (existingPositionAccount) {
            if (!existingPositionAccount.containClass('active')) {
                this.selectPosition(existingPositionAccount);
            }
        } else {
            const activePositionAccount = this.element.findFirst('.current-position-container.active');
            if (activePositionAccount) {
                this.selectPosition(activePositionAccount);
            }

            const tradeComp = this.getTradeComponent();
            tradeComp.setAccount(accountName);

            const advancedTradeComponent = this.getAdvancedTradeComponent();
            advancedTradeComponent?.setAccount(accountName);
        }
    }

    private onPositionClick() {
        if (!this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel?.defaultEnableTradeOrderEntry) {
            return;
        }

        let fromLastTradeAccount = false;
        const positionsContainer = Array.from(this.element.querySelectorAll<HTMLElement>('.current-position-container'));
        if (positionsContainer.some((x: HTMLElement) => x.getAttribute('data-account-name') === this.lastTradeAccount)) {
            this.selectedAccount = this.lastTradeAccount;
            this.lastTradeAccount = '';
            fromLastTradeAccount = true;
        } else {
            const existingPositionAccount = this.selectedAccount && positionsContainer.find((x: HTMLElement) => x.getAttribute('data-account-name') === this.selectedAccount);
            if (!existingPositionAccount) {
                this.selectedAccount = '';
            }
        }

        const setShares = (element: HTMLElement) => {
            const shares = Math.abs(parseInt(element.getAttribute('data-shares') ?? ''));
            this.setShares(shares);
        };

        const setAVOEPShares = (element: HTMLElement) => {
            const shares = Math.abs(parseInt(element.getAttribute('data-shares') ?? ''));
            this.setAVOEPShares(shares);
        };

        positionsContainer.forEach((el: HTMLElement) => {
            if (!el.classList.contains('inited')) {
                el.classList.add('inited');

                if (!this.pnlClickInited) {
                    if (!this.selectedAccount) {
                        this.removePositionActive();
                        if (!this.base.isTouchDevice) {
                            this.selectPosition(el);
                        }
                    }

                    this.pnlClickInited = true;
                }

                if (this.selectedAccount == el.getAttribute('data-account-name')) {
                    if (!el.classList.contains('active')) {
                        this.selectPosition(el);
                    } else if (fromLastTradeAccount) {
                        setShares(el);
                        this.displayExitOrder(false);
                    } else {
                        const tradeComp = this.getTradeComponent();
                        if (tradeComp.getQuoteType() == QuoteType.Shares) {
                            setShares(el);
                        }

                        const advancedTradeComponent = this.getAdvancedTradeComponent();
                        if (advancedTradeComponent?.getQuoteType() == QuoteType.Shares) {
                            setAVOEPShares(el);
                        }
                    }
                }

                el.addEventListener('click', () => {
                    this.turnOffEdit();
                    
                    this.selectPosition(el);
                });
            }
        });
    }

    private turnOffEdit() {
        const advancedTradeComponent = this.getAdvancedTradeComponent();
        advancedTradeComponent?.editOff();

        const tradeOrderTypeComponents = this.base.getComponentsByType(TradeOrderTypeComponent) as Array<TradeOrderTypeComponent>;
        for (const t of tradeOrderTypeComponents) {
            t.editOff();
        }
    }

    private removePositionActive() {
        const positionsContainer = Array.from(this.element.querySelectorAll<HTMLElement>('.current-position-container'));
        positionsContainer.forEach((x: HTMLElement) => {
            x.classList.remove('active');
        });
    }

    private selectPosition(element: HTMLElement) {
        const tradeComp = this.getTradeComponent();
        const advancedTradeComponent = this.getAdvancedTradeComponent();
        if (element.containClass('active')) {
            element.classList.remove('active');
            this.selectedAccount = '';
            this.selectedAccountFlatten = false;

            tradeComp.setAccount(this.preSelectedAccount);
            tradeComp.setQuoteType(this.preSelectedQuoteType);
            tradeComp.setQuoteQuantity(this.preSelectedShares);

            advancedTradeComponent?.setAccount(this.preSelectedAccount);
            advancedTradeComponent?.setQuoteType(this.preSelectedQuoteType);
            advancedTradeComponent?.setQuoteQuantity(this.preSelectedShares);

            const tradeOrderTypeComponents = this.base.getComponentsByType(TradeOrderTypeComponent) as Array<TradeOrderTypeComponent>;
            for (const t of tradeOrderTypeComponents) {
                t.syncValue();
            }
        } else {
            this.removePositionActive();
            element.classList.add('active');
            const accountName = element.getAttribute('data-account-name');
            const shares = Math.abs(parseInt(element.getAttribute('data-shares') ?? ''));
            if (!isNaN(shares) && tradeComp) {
                if (!this.preSelectedInited) {
                    this.preSelectedInited = true;
                    this.preSelectedAccount = tradeComp.getAccountName();
                    this.preSelectedShares = tradeComp.getQuoteQuantity();
                    this.preSelectedQuoteType = tradeComp.getQuoteType();
                }

                this.selectedAccount = accountName;
                tradeComp.setAccount(accountName);
                advancedTradeComponent?.setAccount(accountName);

                this.setShares(shares);
                this.setAVOEPShares(shares);
            }
        }

        this.lastTradeAccount = '';

        const forceHide = !this.selectedAccount;
        this.displayExitOrder(forceHide);
    }

    private setShares(shares: number) {
        const tradeComp = this.getTradeComponent();
        if (this.base.marketingWebChartMode) {
            if (this.base.tiChartSeries?.chartStyleSettings?.persistPositionShareQuantity) {
                tradeComp.setSharesAndPersisting(shares);
            } else {
                tradeComp.setShares(shares);
            }
        } else {
            if (!this.base.tiChartSeries?.chartStyleSettings?.buySellSyncTradeSizeEnabled && this.base.tiChartSeries?.chartStyleSettings?.persistPositionShareQuantity) {
                tradeComp.setSharesAndPersisting(shares);
            } else {
                tradeComp.setShares(shares);
            }
        }
    }

    private setAVOEPShares(shares: number) {
        const advancedTradeComponent = this.getAdvancedTradeComponent();
        advancedTradeComponent?.setShares(shares);
    }

    private toggleTradeOptionButtons(forceHide: boolean) {
        const tradeOptionsComponent = this.base.getComponentsByType(QuickOrdersComponent)?.[0] as QuickOrdersComponent;
        tradeOptionsComponent?.updateSelectedAccount(forceHide ? '' : this.selectedAccount, this.isSelectedAccountClosed);
    }

    private displayExitOrder(forceHide: boolean) {
        if (!this.enableExitOrder) return;

        if (!this.base.isTouchDevice) {
            this.displayExitOrderInRightEdge(forceHide);
        } else {
            this.displayExitOrderOnTopOfBars(forceHide);
        }

        this.toggleTradeOptionButtons(forceHide);
    }

    private displayExitOrderOnTopOfBars(forceHide: boolean) {
        const drawExitOrderComp = this.base.getComponentsByType(DrawExitOrderComponent)?.[0] as DrawExitOrderComponent;
        if (drawExitOrderComp) {
            drawExitOrderComp.update(forceHide);
        }
    }

    private displayExitOrderInRightEdge(forceHide: boolean) {
        const drawExitOrderComp = this.base.getComponentsByType(DrawExitOrderComponent)?.[0] as DrawExitOrderComponent;
        if (drawExitOrderComp) {
            drawExitOrderComp.update(forceHide, true);
        }
    }

    private subscribeChartDataService() {
        this.subscribePnlData();
        this.subscribeCurrentPositionData();
        this.subscribeLastPrice();
        this.subscribeAccountList();

        this.base.tiChartSeries.subscribeChartLoad(this.id, () => {
            this.symbol = this.base.tiChartSeries.symbol;
        });
        this.base.chartService.subscribeChartRequestingNewSymbolData((symbol: string, timeframe: Timeframe) => {
            if (this.symbol === symbol) {
                this.lastTradeAccount = this.selectedAccount;
            }
        });
    }

    private subscribePnlData() {
        this.chartService.subscribePnlData((pnlData: any) => this.updatePnlData(pnlData));
    }

    private subscribeCurrentPositionData() {
        this.chartService.subscribeCurrentPositionData((currentPositionData: any) => this.updateCurrentPositionData(currentPositionData));
        this.chartService.subscribeCurrentPositionViewModel((data: Array<CurrentPositionViewModel>) => {
            this.currentPositionViewModels = data;
            this.updateCurrentPositionFromViewModel();
        });
    }

    private subscribeLastPrice() {
        this.chartService.subscribeLastPriceData((lastPrice: number) => { this.lastPrice = lastPrice });
    }

    private subscribeAccountList() {
        this.base.chartService.subscribe_TradeAccounts((accounts: Array<TradeAccountModel>) => {
            this.accounts = accounts;
        });
    }

    private hidePnlDataElement() {
        const pnlDiv = this.element.findFirst('.pnl');
        pnlDiv?.addClass('hidden');
    }

    private showPnlDataElement() {
        const pnlDiv = this.element.findFirst('.pnl');
        pnlDiv?.removeClass('hidden');
    }

    private updatePnlData(pnlData: PnlDataModel) {
        const pnlDiv = this.element.findFirst('.pnl');

        if (pnlData.doesNotHaveValue()) {
            this.hidePnlDataElement();
            return;
        }

        this.showPnlDataElement();
        pnlDiv?.setInnerHtml(pnlData.pnlText);
        pnlDiv?.setAttribute("style", `color:${pnlData.pnLColor}`);
    }

    private hideCurrentPositionElement() {
        this.element.findFirst('.current-position-wrapper')?.setInnerHtml('');
    }

    private updateCurrentPositionData(currentPositions: Array<CurrentPositionDataModel>) {
        if (!currentPositions.length) {
            this.hideCurrentPositionElement();
        }

        let html = '';
        for (const currentPosition of currentPositions) {
            if (currentPosition.doesNotHaveValue()) {
                continue;
            }

            if (this.base.tiChartSeries.hiddenAccounts?.includes(currentPosition.account)) {
                continue;
            }

            html += currentPosition.toHtml(this.selectedAccount);
        }

        this.element.findFirst('.current-position-wrapper')?.setInnerHtml(html);
        this.addOneClass();
    }

    private updateCurrentPositionFromViewModel() {
        if (!this.currentPositionViewModels.length) {
            this.hideCurrentPositionElement();
        }

        let html = '';
        const chartStyle = this.base.tiChartSeries?.chartStyleSettings;
        const greenColor = this.base.marketingWebChartMode ? chartStyle?.isDarkTheme ? 'rgb(23, 113, 23)' : 'rgb(50, 205, 50)' : '#12fd00';
        const redColor = 'rgb(255, 0, 0)';
        this.selectedAccountFlatten = false;

        for (const currentPosition of this.currentPositionViewModels) {
            if (this.base.tiChartSeries.hiddenAccounts?.includes(currentPosition.accountName)) {
                continue;
            }

            const avgCost = FormatHelpers.numberToStringThousandSeparators(currentPosition.averageCost, true);

            const isClosed = currentPosition.shares === 0;
            const openUnrealizedProfit = (currentPosition.unrealizedProfit == null || isNaN(currentPosition.unrealizedProfit) ? currentPosition.getTotalProfit(this.lastPrice) : currentPosition.unrealizedProfit);
            const totalProfit = isClosed ? currentPosition.realizedProfitLoss : openUnrealizedProfit;
            const isTotalProfitPositive = totalProfit >= 0;
            const totalProfitString = FormatHelpers.numberToStringThousandSeparators(Math.abs(totalProfit), true, 0);

            const color = isTotalProfitPositive || totalProfitString == '0' ? greenColor : redColor;

            let displayAccount = currentPosition.accountName;

            const account = this.accounts.find(x => x.privacyName == currentPosition.accountName);
            if (account) {
                displayAccount = account.privacyName;
            }

            let title = `${displayAccount}. Select to display Position Exit Order`;

            let sharesCount = `<span style="color: ${currentPosition.shares >= 0 ? greenColor : redColor}; font-weight: bold;">${currentPosition.shares >= 0 ? '+' : ''}${currentPosition.shares}</span> shares <span class="at">@</span> ${avgCost}`;
            if (isClosed) {
                sharesCount = `Closed`;
                title = `Flattened ${Math.abs(currentPosition.netSharesTradedToday)} shares,  ${displayAccount}.`;
            }

            if (this.selectedAccount === currentPosition.accountName) {
                this.isSelectedAccountClosed = isClosed;
                this.selectedAccountFlatten = isClosed;
            }

            html += `
                <div class="current-position-container ${isClosed ? 'closed' : ''} ${this.pnlClickInited && this.selectedAccount === currentPosition.accountName ? "active" : ""}" title="${title}" data-account-name="${currentPosition.accountName}" data-shares="${currentPosition.shares}">
                    <div class="shares-count">
                        ${sharesCount}
                    </div>
                    <div class="shares-pnl" style="color: ${color};font-weight: bold;">${isTotalProfitPositive || totalProfitString == '0' ? "+" : "-"}$${totalProfitString}</div>
                </div>
            `;
        }

        this.element.findFirst('.current-position-wrapper')?.setInnerHtml(html);
        this.addOneClass();
        this.onPositionClick();
        this.toggleTradeOptionButtons(false);
    }

    private getTradeComponent = (() => {
        let tradeComp: TradeComponent | null = null;
        return () => {
            if (!tradeComp) {
                tradeComp = this.base.getComponentsByType(TradeComponent)?.[0] as TradeComponent;
            }
            return tradeComp;
        };
    })();

    private avoepTradeComponent: AdvancedTradeComponent | null = null;
    private getAdvancedTradeComponent() {
        if (!this.avoepTradeComponent) {
            this.avoepTradeComponent = this.base.getComponentsByType(AdvancedTradeComponent)?.[0] as AdvancedTradeComponent;
        }

        return this.avoepTradeComponent;
    }

    private resizeTimmer = null as any;
    private onResize() {
        this.base.domEvents.onResize(this.id, () => {
            clearTimeout(this.resizeTimmer);
            this.resizeTimmer = setTimeout(() => {
                this.displayExitOrder(false);
            }, 150);
        });
    }
}
